import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatCurrency } from '../../i18n/i18n';

/**
 * Hiring dialog — two modes:
 * 1. No recruiter active & no candidates: choose a recruiter tier
 * 2. Recruiter searching: show progress
 * 3. Candidates available: show candidate cards with hire buttons
 */
export function openHireStaffDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: 'Recruit Staff',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:8px;min-width:340px;max-height:450px;overflow-y:auto;';

  function render() {
    container.innerHTML = '';
    const state = store.getState();
    const theme = store.getThemeData();

    // Check office capacity
    if (state.staff.length >= state.office.maxStaff) {
      const full = document.createElement('p');
      full.textContent = 'Office is full. Upgrade to hire more staff.';
      full.style.cssText = 'color:var(--color-warning);padding:16px 0;text-align:center;';
      container.appendChild(full);
      return;
    }

    // Mode 1: Recruiter is searching
    if (state.activeRecruiter) {
      const recruiterDef = theme.entities.recruiters.find((r) => r.id === state.activeRecruiter!.tier);
      const info = document.createElement('div');
      info.style.cssText = 'text-align:center;padding:24px 16px;';

      const title = document.createElement('div');
      title.textContent = recruiterDef?.name ?? 'Recruiter';
      title.style.cssText = 'font-size:14px;font-weight:600;margin-bottom:8px;';
      info.appendChild(title);

      const ticksPerWeek = theme.balance.ticksPerDay * theme.balance.daysPerWeek;
      const weeksLeft = Math.ceil(state.activeRecruiter.ticksRemaining / ticksPerWeek);
      const progress = document.createElement('div');
      progress.textContent = `Searching for candidates... ~${weeksLeft} week${weeksLeft !== 1 ? 's' : ''} remaining`;
      progress.style.cssText = 'font-size:12px;color:var(--color-text-secondary);margin-bottom:12px;';
      info.appendChild(progress);

      const bar = document.createElement('div');
      bar.className = 'progress-bar';
      bar.style.cssText = 'height:8px;max-width:200px;margin:0 auto;';
      const total = recruiterDef?.durationTicks ?? 70;
      const pct = Math.min(100, ((total - state.activeRecruiter.ticksRemaining) / total) * 100);
      const fill = document.createElement('div');
      fill.className = 'progress-bar__fill';
      fill.style.cssText = `width:${pct}%;background:var(--color-info);`;
      bar.appendChild(fill);
      info.appendChild(bar);

      const hint = document.createElement('div');
      hint.textContent = 'Close this dialog and keep playing — candidates will arrive automatically.';
      hint.style.cssText = 'font-size:10px;color:var(--color-text-muted);margin-top:12px;';
      info.appendChild(hint);

      container.appendChild(info);
      return;
    }

    // Mode 2: Candidates available — show them
    if (state.hiringPool.length > 0) {
      const poolLabel = document.createElement('div');
      poolLabel.textContent = `${state.hiringPool.length} CANDIDATES`;
      poolLabel.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;';
      container.appendChild(poolLabel);

      const statColors: Record<string, string> = {
        coding: 'var(--color-stat-coding)', art: 'var(--color-stat-art)',
        sound: 'var(--color-stat-sound)', design: 'var(--color-stat-design)',
      };

      for (const candidate of state.hiringPool) {
        const card = document.createElement('div');
        card.className = 'card';
        card.style.cssText = 'display:flex;flex-direction:column;gap:4px;';

        // Name + role + salary
        const topRow = document.createElement('div');
        topRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
        const nameEl = document.createElement('strong');
        nameEl.textContent = candidate.name;
        nameEl.style.fontSize = '12px';
        const roleEl = document.createElement('span');
        roleEl.textContent = `${candidate.role} · ${formatCurrency(candidate.salary)}/wk`;
        roleEl.style.cssText = 'font-size:10px;color:var(--color-text-secondary);';
        topRow.appendChild(nameEl);
        topRow.appendChild(roleEl);
        card.appendChild(topRow);

        // Stats
        const statsRow = document.createElement('div');
        statsRow.style.cssText = 'display:flex;gap:8px;';
        for (const [stat, value] of Object.entries(candidate.stats)) {
          const el = document.createElement('span');
          el.style.cssText = `font-size:10px;color:${statColors[stat] ?? 'var(--color-text-muted)'};`;
          el.textContent = `${stat.slice(0, 3)}: ${Math.round(value)}`;
          statsRow.appendChild(el);
        }
        card.appendChild(statsRow);

        // Traits
        if (candidate.traits.length > 0) {
          const traitsEl = document.createElement('div');
          traitsEl.style.cssText = 'font-size:9px;color:var(--color-xp);';
          const traitNames = candidate.traits.map((tid) => {
            const def = theme.entities.traits.find((t) => t.id === tid);
            return def?.name ?? tid;
          });
          traitsEl.textContent = traitNames.join(', ');
          card.appendChild(traitsEl);
        }

        // Hire button
        const hiringCost = candidate.salary * 2;
        const canAfford = state.company.funds >= hiringCost;
        const bottomRow = document.createElement('div');
        bottomRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-top:2px;';
        const costEl = document.createElement('span');
        costEl.textContent = `Signing: ${formatCurrency(hiringCost)}`;
        costEl.style.cssText = `font-size:10px;color:${canAfford ? 'var(--color-text-muted)' : 'var(--color-danger)'};`;
        const hireBtn = document.createElement('button');
        hireBtn.className = 'btn btn--primary';
        hireBtn.textContent = 'Hire';
        hireBtn.disabled = !canAfford;
        hireBtn.style.cssText = 'font-size:10px;padding:2px 10px;';
        hireBtn.addEventListener('click', () => {
          const result = store.dispatch({ type: 'HIRE_STAFF', staffId: candidate.id });
          if (result.success) {
            const updated = store.getState();
            const hired = updated.staff.find((st) => st.id === candidate.id);
            const team = updated.teams.find((t) => t.memberIds.length < t.maxSize);
            if (hired && team && !hired.teamId) {
              store.dispatch({ type: 'ASSIGN_TO_TEAM', staffId: hired.id, teamId: team.id });
            }
            render();
          }
        });
        bottomRow.appendChild(costEl);
        bottomRow.appendChild(hireBtn);
        card.appendChild(bottomRow);

        container.appendChild(card);
      }

      // Option to search again
      const searchAgain = document.createElement('div');
      searchAgain.style.cssText = 'text-align:center;margin-top:8px;';
      const searchBtn = document.createElement('button');
      searchBtn.className = 'btn';
      searchBtn.textContent = 'Search for new candidates...';
      searchBtn.style.cssText = 'font-size:11px;';
      searchBtn.addEventListener('click', () => {
        showRecruiterSelection();
      });
      searchAgain.appendChild(searchBtn);
      container.appendChild(searchAgain);
      return;
    }

    // Mode 3: No candidates, no recruiter — show recruiter selection
    showRecruiterSelection();
  }

  function showRecruiterSelection() {
    container.innerHTML = '';
    const state = store.getState();
    const theme = store.getThemeData();

    const label = document.createElement('div');
    label.textContent = 'HIRE A RECRUITER';
    label.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;';
    container.appendChild(label);

    const desc = document.createElement('p');
    desc.textContent = 'Choose a recruiter to search for candidates. Better recruiters find higher-quality talent.';
    desc.style.cssText = 'font-size:11px;color:var(--color-text-secondary);margin:0;';
    container.appendChild(desc);

    for (const r of theme.entities.recruiters) {
      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = 'display:flex;flex-direction:column;gap:4px;';

      const topRow = document.createElement('div');
      topRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
      const nameEl = document.createElement('strong');
      nameEl.textContent = r.name;
      nameEl.style.fontSize = '12px';
      const costEl = document.createElement('span');
      costEl.textContent = formatCurrency(r.cost);
      costEl.style.cssText = 'font-size:12px;font-family:var(--font-pixel);color:var(--color-text-secondary);';
      topRow.appendChild(nameEl);
      topRow.appendChild(costEl);
      card.appendChild(topRow);

      const descEl = document.createElement('div');
      descEl.textContent = r.description;
      descEl.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
      card.appendChild(descEl);

      const ticksPerWeek = theme.balance.ticksPerDay * theme.balance.daysPerWeek;
      const weeks = Math.ceil(r.durationTicks / ticksPerWeek);
      const details = document.createElement('div');
      details.style.cssText = 'font-size:10px;color:var(--color-text-secondary);display:flex;gap:8px;';
      details.innerHTML = `<span>${r.candidateCount} candidates</span><span>~${weeks} week${weeks !== 1 ? 's' : ''}</span>`;
      card.appendChild(details);

      const canAfford = state.company.funds >= r.cost;
      const hireBtn = document.createElement('button');
      hireBtn.className = 'btn btn--primary';
      hireBtn.textContent = canAfford ? 'Hire Recruiter' : `Need ${formatCurrency(r.cost)}`;
      hireBtn.disabled = !canAfford;
      hireBtn.style.cssText = 'font-size:11px;margin-top:4px;align-self:flex-end;padding:4px 12px;';
      hireBtn.addEventListener('click', () => {
        const result = store.dispatch({ type: 'HIRE_RECRUITER', tier: r.id });
        if (result.success) {
          render();
        }
      });
      card.appendChild(hireBtn);

      container.appendChild(card);
    }
  }

  render();
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
